/**
 * DiffPage component.
 * Page for viewing and managing configuration diffs between draft and live versions.
 * Provides diff view, validation, and promotion workflow.
 */

import { useEffect, useState, useCallback, useMemo } from "react";

import { DiffView } from "../components/diff";
import type {
  ConfigType,
  ConfigValidationResult,
  ConfigDiffSummary,
} from "../components/diff/types";
import { PromotionDialog, RollbackDialog, VersionHistory } from "../components/promotion";
import type { ChangedFileSummary } from "../components/promotion/types";
import {
  createConfigApiClient,
  useConfigDiff,
  useConfigValidation,
  useVersionHistory,
  usePromotion,
  useRollback,
  type ConfigApiClient,
} from "../hooks/use-config-api";

// ============================================================================
// Types
// ============================================================================

export interface DiffPageProps {
  /** Vertical key */
  readonly verticalKey: string;
  /** Config type */
  readonly configType: ConfigType;
  /** API base URL */
  readonly apiBaseUrl: string;
  /** Function to get auth token */
  readonly getAuthToken: () => string | null;
  /** Callback when promotion succeeds */
  readonly onPromotionSuccess?: () => void;
  /** Callback when rollback succeeds */
  readonly onRollbackSuccess?: () => void;
}

/** Tab types for the page */
type Tab = "diff" | "history";

// ============================================================================
// Component
// ============================================================================

/**
 * DiffPage component for config diff viewing and promotion.
 */
export function DiffPage({
  verticalKey,
  configType,
  apiBaseUrl,
  getAuthToken,
  onPromotionSuccess,
  onRollbackSuccess,
}: DiffPageProps): React.ReactElement {
  // Create API client
  const apiClient = useMemo<ConfigApiClient>(
    () => createConfigApiClient({ baseUrl: apiBaseUrl, getAuthToken }),
    [apiBaseUrl, getAuthToken]
  );

  // Active tab state
  const [activeTab, setActiveTab] = useState<Tab>("diff");

  // Dialog states
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [rollbackTargetVersion, setRollbackTargetVersion] = useState<number | null>(null);

  // Version viewer state
  const [versionViewContent, setVersionViewContent] = useState<{
    content: string;
    version: number;
    compareContent?: string;
    compareVersion?: number;
  } | null>(null);
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);

  // API hooks
  const { diff, liveContent, draftContent, liveVersion, draftVersion, isLoading, error, refresh } =
    useConfigDiff(apiClient, verticalKey, configType);

  const { validation, isValidating, validate } = useConfigValidation(
    apiClient,
    verticalKey,
    configType
  );

  const {
    entries: historyEntries,
    pagination: historyPagination,
    isLoading: isLoadingHistory,
    refresh: refreshHistory,
    loadMore: loadMoreHistory,
  } = useVersionHistory(apiClient, verticalKey, configType);

  const { isPromoting, promote } = usePromotion(apiClient, verticalKey, configType);

  const { isRollingBack, rollback } = useRollback(apiClient, verticalKey, configType);

  // Load data on mount
  useEffect(() => {
    refresh();
    refreshHistory();
  }, [refresh, refreshHistory]);

  // Build changed files summary for promotion dialog
  const changedFiles = useMemo<readonly ChangedFileSummary[]>(() => {
    if (!diff) return [];
    return [
      {
        configType,
        diff,
        validation: validation ?? undefined,
        liveVersion,
        draftVersion: draftVersion ?? 0,
      },
    ];
  }, [configType, diff, validation, liveVersion, draftVersion]);

  // Handlers
  const handleOpenPromotionDialog = useCallback(async (): Promise<void> => {
    // Validate before showing dialog
    try {
      await validate();
    } catch {
      // Validation errors will be shown in dialog
    }
    setShowPromotionDialog(true);
  }, [validate]);

  const handlePromote = useCallback(
    async (changeSummary: string): Promise<void> => {
      await promote(changeSummary);
      await refresh();
      await refreshHistory();
      onPromotionSuccess?.();
    },
    [promote, refresh, refreshHistory, onPromotionSuccess]
  );

  const handleViewVersion = useCallback(
    async (version: number): Promise<void> => {
      setIsLoadingVersion(true);
      try {
        const config = await apiClient.getConfig(verticalKey, configType, version);
        const content = JSON.stringify(config.data, null, 2);
        setVersionViewContent({ content, version });
        setActiveTab("diff");
      } catch {
        // Errors are visible via the version view state remaining null
      } finally {
        setIsLoadingVersion(false);
      }
    },
    [apiClient, verticalKey, configType]
  );

  const handleCompareVersions = useCallback(
    (fromVersion: number, toVersion: number): void => {
      setIsLoadingVersion(true);
      Promise.all([
        apiClient.getConfig(verticalKey, configType, fromVersion),
        apiClient.getConfig(verticalKey, configType, toVersion),
      ])
        .then(([fromConfig, toConfig]) => {
          const fromContent = JSON.stringify(fromConfig.data, null, 2);
          const toContent = JSON.stringify(toConfig.data, null, 2);
          setVersionViewContent({
            content: toContent,
            version: toVersion,
            compareContent: fromContent,
            compareVersion: fromVersion,
          });
          setActiveTab("diff");
        })
        .catch(() => {
          // Error handling — failed to load versions
        })
        .finally(() => {
          setIsLoadingVersion(false);
        });
    },
    [apiClient, verticalKey, configType]
  );

  /** Clear version view and return to live vs draft diff */
  const handleClearVersionView = useCallback((): void => {
    setVersionViewContent(null);
  }, []);

  const handleRequestRollback = useCallback((version: number): void => {
    setRollbackTargetVersion(version);
    setShowRollbackDialog(true);
  }, []);

  const handleRollback = useCallback(
    async (reason: string): Promise<void> => {
      if (rollbackTargetVersion === null) return;
      await rollback(rollbackTargetVersion, reason);
      await refresh();
      await refreshHistory();
      setShowRollbackDialog(false);
      setRollbackTargetVersion(null);
      onRollbackSuccess?.();
    },
    [rollbackTargetVersion, rollback, refresh, refreshHistory, onRollbackSuccess]
  );

  return (
    <div className="diff-page flex flex-col h-full" data-testid="diff-page">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Configuration Changes
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {verticalKey} / {configType}.json
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Validation status indicator */}
            {validation && (
              <ValidationStatusBadge validation={validation} isValidating={isValidating} />
            )}
            {/* Promote button */}
            <button
              type="button"
              onClick={handleOpenPromotionDialog}
              disabled={!diff?.hasChanges || isPromoting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700
                         rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Promote to Live
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mt-4">
          <TabButton
            label="Diff View"
            isActive={activeTab === "diff"}
            onClick={() => setActiveTab("diff")}
          />
          <TabButton
            label="Version History"
            isActive={activeTab === "history"}
            onClick={() => setActiveTab("history")}
            badge={historyPagination?.total}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {error && (
          <div className="m-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            <button
              type="button"
              onClick={refresh}
              className="mt-2 text-sm text-red-600 hover:text-red-700 dark:text-red-400"
            >
              Retry
            </button>
          </div>
        )}

        {isLoading && !diff && (
          <div className="flex items-center justify-center h-full">
            <SpinnerIcon className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        )}

        {/* Diff Tab */}
        {activeTab === "diff" && (
          <div className="h-full flex flex-col">
            {/* Version view banner */}
            {versionViewContent && (
              <div className="flex-shrink-0 px-6 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 flex items-center justify-between">
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  {versionViewContent.compareVersion !== undefined
                    ? `Comparing version ${versionViewContent.compareVersion} with version ${versionViewContent.version}`
                    : `Viewing version ${versionViewContent.version}`}
                </span>
                <button
                  type="button"
                  onClick={handleClearVersionView}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 underline"
                >
                  Back to draft vs live
                </button>
              </div>
            )}

            {isLoadingVersion && (
              <div className="flex items-center justify-center py-8">
                <SpinnerIcon className="w-6 h-6 text-gray-400 animate-spin" />
                <span className="ml-2 text-sm text-gray-500">Loading version...</span>
              </div>
            )}

            {/* Version-specific diff view */}
            {!isLoadingVersion && versionViewContent && (
              <div className="flex-1 min-h-0">
                <DiffView
                  original={versionViewContent.compareContent ?? ""}
                  modified={versionViewContent.content}
                  originalVersion={versionViewContent.compareVersion ?? null}
                  modifiedVersion={versionViewContent.version}
                  language="json"
                  readOnly={true}
                />
              </div>
            )}

            {/* Default draft vs live diff */}
            {!isLoadingVersion && !versionViewContent && diff && (
              <>
                {/* Diff summary */}
                <div className="flex-shrink-0 px-6 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <DiffSummary diff={diff} />
                </div>

                {/* Diff editor */}
                <div className="flex-1 min-h-0">
                  {diff.hasChanges ? (
                    <DiffView
                      original={liveContent ?? ""}
                      modified={draftContent ?? ""}
                      originalVersion={liveVersion}
                      modifiedVersion={draftVersion ?? undefined}
                      language="json"
                      readOnly={true}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <CheckIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
                        <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                          No changes
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Draft matches the live version
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <div className="p-6 h-full overflow-y-auto">
            <VersionHistory
              verticalKey={verticalKey}
              configType={configType}
              entries={historyEntries}
              isLoading={isLoadingHistory}
              liveVersion={liveVersion ?? undefined}
              onViewVersion={handleViewVersion}
              onCompareVersions={handleCompareVersions}
              onRollback={handleRequestRollback}
              pagination={historyPagination ?? undefined}
              onLoadMore={loadMoreHistory}
            />
          </div>
        )}
      </div>

      {/* Promotion Dialog */}
      <PromotionDialog
        isOpen={showPromotionDialog}
        onClose={() => setShowPromotionDialog(false)}
        verticalKey={verticalKey}
        configType={configType}
        changedFiles={changedFiles}
        validationStatus={validation}
        isValidating={isValidating}
        onPromote={handlePromote}
        onValidate={async () => {
          await validate();
        }}
      />

      {/* Rollback Dialog */}
      {rollbackTargetVersion !== null && (
        <RollbackDialog
          isOpen={showRollbackDialog}
          onClose={() => {
            setShowRollbackDialog(false);
            setRollbackTargetVersion(null);
          }}
          verticalKey={verticalKey}
          configType={configType}
          targetVersion={rollbackTargetVersion}
          currentVersion={liveVersion ?? 0}
          onRollback={handleRollback}
          isRollingBack={isRollingBack}
        />
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface TabButtonProps {
  readonly label: string;
  readonly isActive: boolean;
  readonly onClick: () => void;
  readonly badge?: number | undefined;
}

function TabButton({ label, isActive, onClick, badge }: TabButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
        isActive
          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
          : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
      }`}
    >
      {label}
      {badge !== undefined && (
        <span
          className={`px-1.5 py-0.5 text-xs rounded-full ${
            isActive
              ? "bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200"
              : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
          }`}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

interface ValidationStatusBadgeProps {
  readonly validation: ConfigValidationResult;
  readonly isValidating: boolean;
}

function ValidationStatusBadge({
  validation,
  isValidating,
}: ValidationStatusBadgeProps): React.ReactElement {
  if (isValidating) {
    return (
      <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
        <SpinnerIcon className="w-3 h-3 animate-spin" />
        Validating
      </span>
    );
  }

  if (validation.canPromote) {
    return (
      <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full">
        <CheckIcon className="w-3 h-3" />
        Valid
      </span>
    );
  }

  const errorCount =
    validation.schemaErrors.filter((e) => e.severity === "error").length +
    validation.referenceErrors.filter((e) => e.severity === "error").length;

  return (
    <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-full">
      <ErrorIcon className="w-3 h-3" />
      {errorCount} error{errorCount !== 1 ? "s" : ""}
    </span>
  );
}

interface DiffSummaryProps {
  readonly diff: ConfigDiffSummary;
}

function DiffSummary({ diff }: DiffSummaryProps): React.ReactElement {
  return (
    <div className="flex items-center gap-6 text-sm">
      <span className="text-gray-600 dark:text-gray-400">
        {diff.totalChanges} change{diff.totalChanges !== 1 ? "s" : ""}
      </span>
      {diff.addedFields.length > 0 && (
        <span className="text-green-600 dark:text-green-400">+{diff.addedFields.length} added</span>
      )}
      {diff.removedFields.length > 0 && (
        <span className="text-red-600 dark:text-red-400">-{diff.removedFields.length} removed</span>
      )}
      {diff.changedFields.length > 0 && (
        <span className="text-amber-600 dark:text-amber-400">
          ~{diff.changedFields.length} modified
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function SpinnerIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ErrorIcon({ className }: { readonly className: string }): React.ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

export default DiffPage;
